import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";
import axios from "axios";
import note from "../../../assets/agent/last-note.svg";

const LastNote = ({ contact, callType, isOpen, onToggle }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const contactId = contact?.contactId;
  const requestedForRef = useRef(null);

  // Which kind of note is relevant to the phase the agent is currently
  // working in: cold-calls/follow-up show call notes, meeting/recovery
  // show meeting notes. Without this, both got mixed together and the
  // exact same note text appeared twice, tagged (call) and (meeting).
  const relevantType =
    callType === "meeting" || callType === "meeting-recovery"
      ? "meeting"
      : "call";
  const filteredNotes = notes.filter((n) => n.type === relevantType);

  useEffect(() => {
    setNotes([]);
    setError(null);
  }, [contactId]);

  useEffect(() => {
    if (isOpen && contactId) {
      fetchNotes(contactId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contactId]);

  const fetchNotes = async (forContactId) => {
    if (!forContactId) {
      setError("No contact ID available.");
      return;
    }
    requestedForRef.current = forContactId;
    setLoading(true);
    setError(null);
    try {
      const accessToken = await getValidToken();
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_APIBASEURL}/api/calls/${forContactId}/notes`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await axios.request(config);
      if (requestedForRef.current !== forContactId) return;
      setNotes(response.data);
    } catch (err) {
      if (requestedForRef.current !== forContactId) return;
      setError("Failed to fetch notes.");
    } finally {
      if (requestedForRef.current === forContactId) {
        setLoading(false);
      }
    }
  };

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center justify-center gap-2 transition-all duration-300 cursor-pointer group"
      >
        <img
          src={note}
          alt="note"
          className="w-[1.5rem] h-[1.5rem] transform transition-transform duration-300 relative"
          style={
            isOpen
              ? {
                  filter:
                    "invert(33%) sepia(93%) saturate(5696%) hue-rotate(197deg) brightness(98%) contrast(101%)",
                }
              : {}
          }
        />
        <div className="absolute right-0 top-1/2 -translate-y-14 w-[150px] px-2 py-1 bg-white text-primary text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          {t("agent.tooltip.note")}
        </div>
      </button>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 px-4" onClick={onToggle}>
          <div
            className="bg-white shadow-2xl rounded-lg p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="text-secondary text-lg mb-4 font-semibold">
              {t("agent.lastNote.title", "Historial de notas")}
            </h5>
            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}
            {!loading &&
              !error &&
              filteredNotes.map((note, index) => (
                <div key={index} className="mb-4">
                  <div className="flex gap-2">
                    <p className="text-blue-500 font-medium">
                      {new Date(note.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center mt-2">
                    <p className="text-primary leading-6">{note.content}</p>
                  </div>
                </div>
              ))}
            {!loading && !error && filteredNotes.length === 0 && (
              <p className="text-gray-500">No notes available.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LastNote;